import AppCommandBar from 'app/AppCommandBar'
import { AccountContext } from 'auth'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { NewsForm, NewsFormSchema, NewsFormValues } from 'forms/NewsForm'
import { useCustomSearchCommand, useShowCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import { pick } from 'lodash'
import { CheckType, News, UserRole } from 'model'
import { PanelType, SelectionMode } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useReadNewsQuery } from 'views/AdminNewsView/AdminNewsView.generated'
import { NewsList } from 'views/AdminNewsView/NewsList'
import { useUpdateWikiCheckMutation } from 'views/AdminWikiView/AdminWikiView.generated'

export const NewsView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [routeParams, updateRouteParams] = useRouteParams('news', {
    search: undefined,
  })
  const router = useRouter()
  const paramsNew = router.getState()
  const newElementsCount = paramsNew.params.newsCounter || 0
  const [updateWikiCheck] = useUpdateWikiCheckMutation()

  const currentMicrosoftId: { homeAccount: string } =
    React.useContext(AccountContext)
  // const modifiedNewsCounter = useNewNewsContext()
  React.useEffect(() => {
    const updateAsync = async () => {
      await updateWikiCheck({
        variables: {
          update: {
            user: currentMicrosoftId.homeAccount,
            type: CheckType.News,
          },
        },
      })
    }
    updateAsync()
    // modifiedNewsCounter.setRefetchFlag(oldValue => !oldValue)
    // WARNING! Do NOT put modifiedNewsCounter in dependency array.
    // Page will do infinite requests when you do this!
  }, [currentMicrosoftId.homeAccount, updateWikiCheck])
  const { loading, data, error } = useReadNewsQuery({
    variables: {
      search: routeParams.search,
    },
  })
  const itemsUnsorted: News[] =
    !loading && data && data.multinews
      ? (data.multinews as News[]).filter((x) => !x._deleted)
      : []
  const items = itemsUnsorted.sort((a: News, b: News) => {
    const firstDate = a.created! as Date
    const secondDate = b.created! as Date
    const result = firstDate < secondDate ? -1 : firstDate > secondDate ? 1 : 0
    return result
  })
  const [selection, selectedItems] = useSelection<News>({
    getKey: (item) => item._id!,
  })

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.news.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => {
        updateRouteParams({ search: undefined })
      },
    },
    [routeParams, updateRouteParams]
  )
  const handleOpen = () => {
    let provingGroundArrayId: string[] = []
    if (selectedItems[0].provingGround.length !== 0) {
      provingGroundArrayId = selectedItems[0].provingGround.map(
        (prGroundElement) => prGroundElement._id
      )
    } else {
      provingGroundArrayId = ['']
    }
    const existingItem = {
      ...pick(selectedItems[0], [
        'title',
        'provingGround',
        'deletionRule',
        'description',
        'body',
        'files',
        'uploadFiles',
        'inactive',
      ]),
      provingGround: provingGroundArrayId,
    } as NewsFormValues
    showPanel(existingItem)
  }
  useShowCommand(
    { name: 'news', count: selectedItems.length },
    {
      priority: 20,
      onClick: handleOpen,
    },
    [selectedItems]
  )

  return (
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.Reader,
        UserRole.User,
        UserRole.ProvingCreator,
        UserRole.Maintenancehead,
        UserRole.ServiceTeam,
        UserRole.ProvingOrganisation,
      ]}
    >
      <StickyPane>
        <FlexSpinner loading={loading} error={error}>
          <AppCommandBar />
          <NewsList
            styledElementsCount={newElementsCount}
            items={items}
            selection={selection}
            selectionMode={SelectionMode.single}
            onItemInvoked={handleOpen}
          />
          <FormikPanel
            {...panelState}
            type={PanelType.medium}
            onDismiss={(e) => {
              if (e) {
                hidePanel()
              }
            }}
            validationSchema={new NewsFormSchema()}
            headerText={t('model.news.label')}
          >
            <NewsForm disabled={true} filesUploading={false} />
          </FormikPanel>
        </FlexSpinner>
      </StickyPane>
    </ViewAuthorizer>
  )
}
